import { Component, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { AuthService } from '../../../services/auth.service';

@Component({
  selector: 'app-register',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    InputTextModule,
    ButtonModule,
    ToastModule
  ],
  providers: [MessageService],
  templateUrl: './register.component.html',
  styleUrl: './register.component.css'
})
export class RegisterComponent {
  firstName = signal('');
  lastName = signal('');
  email = signal('');
  phone = signal('');
  password = signal('');
  confirmPassword = signal('');
  transactionPin = signal('');
  confirmTransactionPin = signal('');
  loading = signal(false);

  constructor(
    private router: Router,
    private authService: AuthService,
    private messageService: MessageService
  ) {}

  onSubmit(): void {
    // Validation
    if (!this.firstName().trim() || !this.lastName().trim()) {
      this.showErrorToast('Please enter your first and last name');
      return;
    }

    if (!this.email().trim()) {
      this.showErrorToast('Please enter your email address');
      return;
    }

    if (!this.isValidEmail(this.email())) {
      this.showErrorToast('Please enter a valid email address');
      return;
    }

    if (!this.phone().trim()) {
      this.showErrorToast('Please enter your phone number');
      return;
    }

    if (!/^\d+$/.test(this.phone().trim())) {
      this.showErrorToast('Phone number must contain only numbers');
      return;
    }

    if (!this.password().trim()) {
      this.showErrorToast('Please enter a password');
      return;
    }

    if (this.password().length < 6) {
      this.showErrorToast('Password must be at least 6 characters long');
      return;
    }

    if (this.password() !== this.confirmPassword()) {
      this.showErrorToast('Passwords do not match');
      return;
    }

    if (!this.transactionPin().trim()) {
      this.showErrorToast('Please enter a transaction PIN');
      return;
    }

    if (!/^\d{4}$/.test(this.transactionPin().trim())) {
      this.showErrorToast('Transaction PIN must be exactly 4 digits');
      return;
    }

    if (this.transactionPin() !== this.confirmTransactionPin()) {
      this.showErrorToast('Transaction PINs do not match');
      return;
    }

    this.loading.set(true);

    this.authService.register({
      firstName: this.firstName().trim(),
      lastName: this.lastName().trim(),
      emailAddress: this.email().trim(),
      phoneNumber: this.phone().trim() || undefined,
      password: this.password().trim(),
      transactionPin: this.transactionPin().trim()
    }).subscribe({
      next: (response) => {
        this.loading.set(false);
        if (response.success) {
          this.showSuccessToast('Registration successful! Redirecting to email verification...');
          // Redirect to email verification with email in query params
          setTimeout(() => {
            this.router.navigate(['/verify-email'], {
              queryParams: { email: this.email().trim() }
            });
          }, 1500);
        } else {
          this.showErrorToast(response.message || 'Registration failed. Please try again.');
        }
      },
      error: (err) => {
        this.loading.set(false);
        this.showErrorToast(err.error?.message || 'An error occurred. Please try again.');
      }
    });
  }

  private showSuccessToast(message: string): void {
    this.messageService.add({
      severity: 'success',
      summary: 'Success',
      detail: message,
      life: 3000
    });
  }

  private showErrorToast(message: string): void {
    this.messageService.add({
      severity: 'error',
      summary: 'Error',
      detail: message,
      life: 4000
    });
  }

  onPhoneInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    // Remove any non-numeric characters
    const numericValue = input.value.replace(/\D/g, '');
    this.phone.set(numericValue);
    // Update the input value to reflect the filtered value
    input.value = numericValue;
  }

  private isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }
}
